import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Lock, Star, Search, Calendar } from 'lucide-react';
import PageQuote from '@/components/PageQuote';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [
  { key: 'basic', label: 'BASIC', color: 'from-blue-600 to-blue-800' },
  { key: 'comps_ready', label: "COMP'S READY", color: 'from-purple-600 to-purple-800' },
  { key: 'advanced', label: 'ADVANCED', color: 'from-orange-600 to-orange-800' }
];

export default function PublicationCenter() {
  const [publications, setPublications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [centerContent, setCenterContent] = useState(null);
  const [subscriptionPrice, setSubscriptionPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pubsRes, catsRes, contentRes, pricingRes] = await Promise.all([
        axios.get(`${API}/publications/published`),
        axios.get(`${API}/test-categories`),
        axios.get(`${API}/publication-center/content`),
        axios.get(`${API}/publication-subscription/pricing`)
      ]);
      
      setPublications(pubsRes.data);
      setCategories(catsRes.data.filter(cat => cat.enabled));
      setCenterContent(contentRes.data);
      setSubscriptionPrice(pricingRes.data.price);
    } catch (error) {
      console.error('Failed to load publications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/publication-subscription/checkout`, { origin_url: origin }, { withCredentials: true });
      window.location.href = res.data.url;
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/');
      } else {
        alert(error.response?.data?.detail || 'Failed to create checkout');
      }
    }
  };

  const handlePurchase = async (publicationId) => {
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/publications/${publicationId}/checkout`, { origin_url: origin }, { withCredentials: true });
      window.location.href = res.data.url;
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/');
      } else {
        alert(error.response?.data?.detail || 'Failed to create checkout');
      }
    }
  };

  const filteredPublications = publications.filter(pub => {
    const matchesSearch = !searchTerm || 
      pub.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pub.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pub.author.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTier = !selectedTier || pub.tier === selectedTier;
    const matchesCategory = !selectedCategory || pub.category === selectedCategory;
    
    return matchesSearch && matchesTier && matchesCategory;
  });

  const groupedByTier = TIERS.reduce((acc, tier) => {
    acc[tier.key] = filteredPublications.filter(pub => pub.tier === tier.key);
    return acc;
  }, {});

  return (
    <Layout>
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#8B5A3C] to-[#6B4423] text-white py-16 mb-12">
        <div className="container mx-auto px-6 text-center">
          <BookOpen className="w-20 h-20 mx-auto mb-6" />
          <h1 className="text-6xl font-bold retro-title mb-6">
            {centerContent?.title || 'PUBLICATION CENTER'}
          </h1>
          <p className="text-xl max-w-4xl mx-auto mb-8 leading-relaxed">
            {centerContent?.description || 'Access professional publications, research, and study materials'}
          </p>
          
          {/* Subscribe Button */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-4 mb-4">
              <Star className="w-8 h-8 text-yellow-400" />
              <h3 className="text-3xl font-bold">Unlimited Access</h3>
              <Star className="w-8 h-8 text-yellow-400" />
            </div>
            <p className="text-2xl mb-4">
              ${subscriptionPrice ? subscriptionPrice.toFixed(2) : '29.99'}/month
            </p>
            <p className="text-lg opacity-90 mb-6">
              Get instant access to all publications in the center
            </p>
            <button
              onClick={handleSubscribe}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-12 py-4 rounded-lg font-bold text-xl hover:shadow-2xl transition-all transform hover:scale-105"
            >
              Subscribe Now
            </button>
          </div>
        </div>
      </div>

      {/* My Publications Button */}
      <div className="container mx-auto px-6 mb-8">
        <button
          onClick={() => navigate('/my-publications')}
          className="bg-gradient-to-r from-[#8B5A3C] to-[#6B4423] text-white px-8 py-3 rounded-lg font-bold hover:shadow-xl transition-all flex items-center gap-2"
        >
          <BookOpen className="w-5 h-5" />
          My Publications
        </button>
      </div>

      {/* Quote Position 1 - Above Main Content */}
      <PageQuote pageName="publication_center" position={1} variant="compact" />

      {/* Search and Filters */}
      <div className="container mx-auto px-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title, author, or keyword..."
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#8B5A3C]"
            />
          </div>
          <select
            value={selectedTier || ''}
            onChange={(e) => setSelectedTier(e.target.value || null)}
            className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#8B5A3C]"
          >
            <option value="">All Tiers</option>
            {TIERS.map(tier => (
              <option key={tier.key} value={tier.key}>{tier.label}</option>
            ))}
          </select>
          <select
            value={selectedCategory || ''}
            onChange={(e) => setSelectedCategory(e.target.value || null)}
            className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#8B5A3C]"
          >
            <option value="">All Categories</option>
            {categories.map((cat, index) => (
              <option key={`${cat.tier}-${cat.slug}-${index}`} value={cat.slug}>{cat.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Publications by Tier */}
      <div className="container mx-auto px-6 pb-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B5A3C] mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-12">
            {TIERS.map(tier => {
              const tierPubs = groupedByTier[tier.key];
              if (tierPubs.length === 0) return null;

              return (
                <div key={tier.key}>
                  <div className={`bg-gradient-to-r ${tier.color} text-white py-6 px-8 rounded-lg mb-6`}>
                    <h2 className="text-3xl font-bold retro-title">{tier.label}</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {tierPubs.map(pub => {
                      const isFree = pub.pricing_type === 'free' || pub.free_trial;
                      const price = pub.price || 0;

                      return (
                        <div
                          key={pub.id}
                          className="bg-white rounded-lg shadow-lg border-4 border-gray-200 hover:border-[#8B5A3C] transition-all overflow-hidden"
                        >
                          {pub.cover_image_url && (
                            <img
                              src={pub.cover_image_url}
                              alt={pub.title}
                              className="w-full h-48 object-cover"
                            />
                          )}
                          <div className="p-6">
                            <div className="flex items-start justify-between mb-3">
                              <h3 className="text-xl font-bold text-gray-800 flex-1">
                                {pub.title}
                              </h3>
                              {isFree ? (
                                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                                  {pub.free_trial ? 'FREE TRIAL' : 'FREE'}
                                </span>
                              ) : pub.pricing_type === 'single_purchase' ? (
                                <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                                  ${price.toFixed(2)}
                                </span>
                              ) : (
                                <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                                  <Lock className="w-3 h-3" />
                                  SUBSCRIBE
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">By {pub.author}</p>
                            <p className="text-gray-700 mb-4 line-clamp-3">{pub.description}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                              <Calendar className="w-4 h-4" />
                              <span>Published {new Date(pub.published_at).toLocaleDateString()}</span>
                            </div>
                            {isFree ? (
                              <button
                                onClick={() => navigate(`/publications/${pub.id}/read`)}
                                className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-3 rounded-lg font-bold hover:shadow-xl transition-all"
                              >
                                Read Now
                              </button>
                            ) : pub.pricing_type === 'single_purchase' ? (
                              <button
                                onClick={() => handlePurchase(pub.id)}
                                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 rounded-lg font-bold hover:shadow-xl transition-all"
                              >
                                Purchase - ${price.toFixed(2)}
                              </button>
                            ) : (
                              <button
                                onClick={handleSubscribe}
                                className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-3 rounded-lg font-bold hover:shadow-xl transition-all flex items-center justify-center gap-2"
                              >
                                <Lock className="w-4 h-4" />
                                Subscribe to Access
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {filteredPublications.length === 0 && (
              <div className="text-center py-20">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-xl text-gray-600">No publications found</p>
              </div>
            )}
          </div>
        )}

        {/* Copyright Warning */}
        <div className="mt-16 bg-red-50 border-4 border-red-500 p-8 rounded-lg">
          <h3 className="text-2xl font-bold text-red-800 mb-4">⚠️ Important Notice</h3>
          <p className="text-gray-800 leading-relaxed">
            All publications are for <strong>view only</strong> while on this website. Publications may not be downloaded, printed, 
            screenshot, or otherwise redistributed in any form or fashion without AcuErnie's expressed written consent. 
            Unauthorized distribution or reproduction of these materials is strictly prohibited and may result in legal action.
          </p>
        </div>
      </div>

      {/* Quote Position 2 - Below Main Content */}
      <PageQuote pageName="publication_center" position={2} variant="compact" />
    </Layout>
  );
}
